import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { User, Mailbox } from '../types';
import MailboxList from './MailboxList';
import MailboxView from './MailboxView';
import AdminPanel from './AdminPanel';
import TokenCountdown from './TokenCountdown';
import api, { AiStatus } from '../services/api';
import { setMailboxAccessToken } from '../services/mailboxAccess';
import createRipple from '../utils/ripple';
import './MainScreen.css';

interface MainScreenProps {
  user: User;
  onLogout: () => void;
  onShowNotification?: (message: string, type: 'error' | 'success') => void;
}

type MainView = 'mailboxes' | 'mailbox' | 'admin';

const MainScreen: React.FC<MainScreenProps> = ({ user, onLogout, onShowNotification }) => {
  const [currentView, setCurrentView] = useState<MainView>('mailboxes');
  const [selectedMailbox, setSelectedMailbox] = useState<string | null>(null);
  const [selectedMailboxData, setSelectedMailboxData] = useState<Mailbox | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingMailboxData, setPendingMailboxData] = useState<{ id: string, data: Mailbox } | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordSubmitPressed, setPasswordSubmitPressed] = useState(false);
  const passwordSubmitPressedTimerRef = useRef<number | null>(null);
  const passwordSubmitBtnRef = useRef<HTMLButtonElement | null>(null);
  const [headerDirection, setHeaderDirection] = useState<1 | -1>(1);
  const [mailboxCopyPressed, setMailboxCopyPressed] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  const lastCopyTriggerAtRef = useRef(0);
  const copiedResetTimerRef = useRef<number | undefined>(undefined);

  /* Инлайн-подтверждение копирования: иконка в пилюле становится галочкой */
  const markEmailCopied = () => {
    setEmailCopied(true);
    window.clearTimeout(copiedResetTimerRef.current);
    copiedResetTimerRef.current = window.setTimeout(() => setEmailCopied(false), 1600);
  };
  const overlayMouseDownOutsideRef = useRef(false);

  useEffect(() => {
    // Прогреваем лого заранее, чтобы избежать визуальной "подгрузки" при переключениях.
    const logo = new Image();
    logo.src = '/logo.png';
    if (typeof logo.decode === 'function') {
      logo.decode().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (passwordSubmitPressedTimerRef.current) {
        window.clearTimeout(passwordSubmitPressedTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!user.isAdmin) {
      setAiStatus(null);
      return undefined;
    }

    let cancelled = false;
    const loadAiStatus = async (force = false) => {
      try {
        const response = await api.getAdminAiStatus(force);
        if (!cancelled && response.success && response.data?.ai) {
          setAiStatus(response.data.ai);
        }
      } catch (error) {
        console.error('AI status check failed:', error);
        if (!cancelled) {
          setAiStatus({
            configured: false,
            isAvailable: false,
            isCritical: true,
            isLow: false,
            shouldShowBanner: true,
            message: 'Не удалось проверить AI usage. Распознавание нестандартных кодов может быть недоступно.',
            currency: 'USD',
            totalBalance: 0,
            grantedBalance: 0,
            toppedUpBalance: 0,
            estimatedChecksAtRealUsage: 0,
            estimatedChecksAtHeavyUsage: 0,
            checkedAt: new Date().toISOString(),
            error: 'request_failed'
          });
        }
      }
    };

    void loadAiStatus(true);
    const intervalId = window.setInterval(() => {
      void loadAiStatus(false);
    }, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [user.isAdmin]);

  // "click" иногда может теряться из-за анимаций/перекрытий.
  // Дублируем триггер на pointerup и защищаемся от двойного вызова.
  const triggerCopy = () => {
    const now = Date.now();
    if (now - lastCopyTriggerAtRef.current < 600) return;
    lastCopyTriggerAtRef.current = now;
    void handleCopyEmail();
  };

  const headerKind = useMemo<'home' | 'mailbox' | 'admin'>(() => {
    if (currentView === 'admin') return 'admin';
    if (currentView === 'mailbox') return 'mailbox';
    return 'home';
  }, [currentView]);

  const headerKey = useMemo(() => {
    if (headerKind === 'home') return 'header-home';
    if (headerKind === 'admin') return 'header-admin';
    // важно: ключ меняется при переключении ящиков, иначе анимации не будет
    return `header-mailbox:${selectedMailbox ?? selectedMailboxData?.id ?? selectedMailboxData?.name ?? ''}`;
  }, [headerKind, selectedMailbox, selectedMailboxData?.id, selectedMailboxData?.name]);

  const headerVariants = useMemo(() => {
    return {
      enter: (dir: 1 | -1) => ({
        y: dir === 1 ? '-110%' : '110%',
        pointerEvents: 'none' as const
      }),
      center: {
        y: '0%',
        pointerEvents: 'auto' as const
      },
      exit: (dir: 1 | -1) => ({
        y: dir === 1 ? '110%' : '-110%',
        pointerEvents: 'none' as const
      })
    };
  }, []);

  /* Обычная функция-рендер, НЕ компонент: компонент, объявленный внутри
     MainScreen, получал новую идентичность на каждом рендере, React пересоздавал
     кнопку адреса с нуля — слетали :active и CSS-переходы нажатия */
  const renderHeaderContent = (interactive: boolean) => {
    if (headerKind === 'home') {
      return (
        <span className="header-title-anim">
          <span
            className="header-logo-mark"
            aria-hidden="true"
          />
          Почтальон Печкин
        </span>
      );
    }

    if (headerKind === 'admin') {
      return <span className="header-title-anim">Админ панель</span>;
    }

    if (headerKind === 'mailbox' && selectedMailboxData) {
      return (
        <button
          type="button"
          className={`mailbox-header-btn ${mailboxCopyPressed ? 'is-pressed' : ''}`}
          onPointerDown={interactive ? () => setMailboxCopyPressed(true) : undefined}
          onPointerUp={interactive ? () => {
            setMailboxCopyPressed(false);
            triggerCopy();
          } : undefined}
          onPointerCancel={interactive ? () => setMailboxCopyPressed(false) : undefined}
          onPointerLeave={interactive ? () => setMailboxCopyPressed(false) : undefined}
          onClick={interactive ? triggerCopy : undefined}
          disabled={!interactive}
          aria-label="Скопировать email адрес"
        >
          <span className={`mailbox-email ${emailCopied ? 'copied' : ''}`}>
            {selectedMailboxData.name}@pe4kin.com
            <i
              className={`mailbox-copy-icon fas ${emailCopied ? 'fa-check' : 'fa-copy'}`}
              aria-hidden="true"
            />
          </span>
        </button>
      );
    }

    return null;
  };

  const handleMailboxSelect = (mailboxId: string, mailboxData?: Mailbox) => {
    // Владелец должен заходить в свои ящики без пароля.
    if (mailboxData?.hasPassword && mailboxData?.isOwner === false) {
      // Если ящик защищён паролем, показываем модальное окно
      setPendingMailboxData({ id: mailboxId, data: mailboxData });
      setShowPasswordModal(true);
      setPasswordInput('');
    } else {
      // Всегда: при открытии ящика и при переключении между ящиками
      // новый адрес едет сверху, старый уезжает вниз.
      setHeaderDirection(1);

      // Если ящик не защищён, открываем сразу
      setSelectedMailbox(mailboxId);
      if (mailboxData) {
        setSelectedMailboxData(mailboxData);
      }
      setCurrentView('mailbox');
    }
  };

  const pulsePasswordSubmit = () => {
    if (passwordSubmitPressedTimerRef.current) {
      window.clearTimeout(passwordSubmitPressedTimerRef.current);
    }
    setPasswordSubmitPressed(true);
    passwordSubmitPressedTimerRef.current = window.setTimeout(() => {
      setPasswordSubmitPressed(false);
    }, 160);
  };

  const handleBackToList = () => {
    // При закрытии ящика: адрес уезжает вверх, снизу возвращается лого+название.
    setHeaderDirection(-1);
    setSelectedMailbox(null);
    setSelectedMailboxData(null);
    setCurrentView('mailboxes');
  };

  const handleMailboxDeleted = (mailboxId: string) => {
    if (String(selectedMailbox || '') !== String(mailboxId)) return;
    setHeaderDirection(-1);
    setSelectedMailbox(null);
    setSelectedMailboxData(null);
    setCurrentView('mailboxes');
  };

  const handleCopyEmail = async () => {
    if (selectedMailboxData) {
      const fullEmail = `${selectedMailboxData.name}@pe4kin.com`;
      try {
        // navigator.clipboard может быть недоступен в некоторых условиях (permissions/focus),
        // поэтому держим надежный fallback.
        if (navigator.clipboard?.writeText && window.isSecureContext) {
          await navigator.clipboard.writeText(fullEmail);
        } else {
          throw new Error('clipboard_unavailable');
        }
        markEmailCopied();
        onShowNotification?.(`Email адрес скопирован: ${fullEmail}`, 'success');
      } catch (err) {
        try {
          const ta = document.createElement('textarea');
          ta.value = fullEmail;
          ta.setAttribute('readonly', 'true');
          ta.style.position = 'fixed';
          ta.style.top = '-1000px';
          ta.style.left = '-1000px';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          const ok = document.execCommand('copy');
          document.body.removeChild(ta);
          if (ok) {
            markEmailCopied();
            onShowNotification?.(`Email адрес скопирован: ${fullEmail}`, 'success');
            return;
          }
        } catch (fallbackErr) {
          console.error('Ошибка копирования (fallback):', fallbackErr);
        }

        console.error('Ошибка копирования:', err);
        onShowNotification?.('Не удалось скопировать email адрес', 'error');
      }
    }
  };

  const handleAdminPanel = () => {
    setHeaderDirection(1);
    setCurrentView('admin');
  };

  const handlePasswordSubmit = async () => {
    if (!pendingMailboxData) return;
    
    try {
      // Проверяем пароль через API
      const response = await api.verifyMailboxPassword(pendingMailboxData.id, passwordInput);
      
      if (response.success) {
        const token = (response.data as any)?.mailboxAccessToken;
        if (typeof token === 'string' && token) {
          setMailboxAccessToken(pendingMailboxData.id, token);
        }
        // Пароль верный - открываем ящик
        setHeaderDirection(1);
        setSelectedMailbox(pendingMailboxData.id);
        setSelectedMailboxData(pendingMailboxData.data);
        setCurrentView('mailbox');
        setShowPasswordModal(false);
        setPendingMailboxData(null);
        setPasswordInput('');
        onShowNotification?.('Ящик открыт!', 'success');
      } else {
        // Неверный пароль
        onShowNotification?.(response.message || 'Неверный пароль', 'error');
      }
    } catch (error) {
      console.error('Password verification error:', error);
      onShowNotification?.('Ошибка при проверке пароля', 'error');
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPendingMailboxData(null);
    setPasswordInput('');
  };

  const renderView = () => {
    // На мобильных - один экран за раз
    if (window.innerWidth < 768) {
      switch (currentView) {
        case 'mailboxes':
          return (
            <MailboxList 
              onMailboxSelect={handleMailboxSelect}
              user={user}
              onShowNotification={onShowNotification}
              onMailboxDeleted={handleMailboxDeleted}
            />
          );
        case 'mailbox':
          return (
            <MailboxView 
              mailboxId={selectedMailbox!}
              onBack={handleBackToList}
            />
          );
        case 'admin':
          return (
            <AdminPanel 
              onBack={handleBackToList}
              onShowNotification={onShowNotification}
            />
          );
        default:
          return null;
      }
    }
    
    // На широких экранах - двухколоночный layout
    if (currentView === 'admin') {
      return (
        <AdminPanel 
          onBack={handleBackToList}
          onShowNotification={onShowNotification}
        />
      );
    }
    
    return (
      <>
        {/* Левая колонка - список ящиков */}
        <div className="sidebar">
          <MailboxList 
            onMailboxSelect={handleMailboxSelect}
            user={user}
            selectedMailbox={selectedMailbox}
            onShowNotification={onShowNotification}
            onMailboxDeleted={handleMailboxDeleted}
          />
        </div>
        
        {/* Правая колонка - содержимое ящика */}
        <div className="main-panel">
          {selectedMailbox ? (
            <MailboxView 
              mailboxId={selectedMailbox}
              onBack={handleBackToList}
              isWideLayout={true}
            />
          ) : (
            <div className="empty-panel">
              <div className="empty-panel-icon" aria-hidden="true">
                <i className="fas fa-inbox"></i>
              </div>
              <h3>Выберите ящик</h3>
              <p>Выберите почтовый ящик из списка слева для просмотра писем</p>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div
      className={`main-screen ${aiStatus?.shouldShowBanner ? 'ai-banner-visible' : ''}`}
      style={{
        ['--header-logo-mask' as any]: "url('/logo.png')",
        ['--header-logo-src' as any]: "url('/logo.png')"
      }}
    >
      <img className="header-logo-preload" src="/logo.png" alt="" aria-hidden="true" />
      {/* Header */}
      <div className="main-header">
        <div className="header-left" data-has-back={currentView !== 'mailboxes'}>
          {currentView !== 'mailboxes' && (
            <button 
              className="back-btn"
              onClick={currentView === 'admin' ? handleBackToList : handleBackToList}
              aria-label="Назад"
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
          )}
          <h1 className="header-title">
            <span className="header-title-stack">
              {/* Сайзер держит размеры стабильными и убирает “дёрганье” ширины/высоты */}
              <span className="header-title-sizer" aria-hidden="true">
                {renderHeaderContent(false)}
              </span>

              <AnimatePresence initial={false} custom={headerDirection}>
                <motion.span
                  key={headerKey}
                  className="header-title-layer"
                  variants={headerVariants}
                  custom={headerDirection}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    duration: 0.18,
                    ease: [0.2, 0.9, 0.2, 1]
                  }}
                >
                  {renderHeaderContent(true)}
                </motion.span>
              </AnimatePresence>
            </span>
          </h1>
        </div>
        
        <div className="header-right">
          {user.isAdmin ? (
            <button 
              className="admin-btn"
              onClick={handleAdminPanel}
            >
              <i className="fas fa-cog"></i>
            </button>
          ) : (
            <TokenCountdown onExpired={onLogout} />
          )}
          <button className="logout-btn" onClick={onLogout}>
            Выход
          </button>
        </div>
      </div>

      {user.isAdmin && aiStatus?.shouldShowBanner && (
        <div className={`ai-usage-banner ${aiStatus.isCritical ? 'critical' : 'low'}`} role="status">
          <i className="fas fa-exclamation-triangle" aria-hidden="true"></i>
          <div className="ai-usage-banner-text">
            <strong>{aiStatus.isCritical ? 'AI usage закончился' : 'AI баланс почти закончился'}</strong>
            <span>
              {aiStatus.message}
              {aiStatus.totalBalance > 0
                ? ` Осталось примерно ${aiStatus.estimatedChecksAtRealUsage.toLocaleString('ru-RU')} AI-проверок.`
                : ''}
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="main-content">
        {renderView()}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div
          className="modal-overlay"
          onMouseDown={(e) => {
            // Закрываем только если "чистый" клик был по оверлею.
            // Drag/выделение из инпута наружу не должен закрывать модалку.
            overlayMouseDownOutsideRef.current = e.target === e.currentTarget;
          }}
          onMouseUp={(e) => {
            if (overlayMouseDownOutsideRef.current && e.target === e.currentTarget) {
              handlePasswordCancel();
            }
            overlayMouseDownOutsideRef.current = false;
          }}
        >
          <div className="password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Введите пароль</h3>
              <i className="fas fa-lock"></i>
            </div>
            <p className="modal-subtitle">
              Ящик "{pendingMailboxData?.data.name}" защищён паролем
            </p>
            <div className="password-form">
              <div className="input-container">
                <i className="fas fa-key input-icon"></i>
                <input
                  // Use text + CSS masking to avoid browser password manager prompts.
                  type="text"
                  className="mailbox-password-input"
                  placeholder="Пароль от ящика"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    if (!passwordInput.trim()) return;
                    pulsePasswordSubmit();
                    handlePasswordSubmit();
                  }}
                  // Prevent password managers / weak password prompts as much as possible.
                  name="mailbox_access_password"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-bwignore="true"
                  autoFocus
                />
              </div>
              <div className="modal-buttons">
                <button className="cancel-btn" onClick={handlePasswordCancel}>
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
                <button 
                  className={`submit-btn ${passwordSubmitPressed ? 'force-pressed' : ''}`}
                  onClick={handlePasswordSubmit}
                  onPointerDown={createRipple}
                  disabled={!passwordInput.trim()}
                  ref={passwordSubmitBtnRef}
                >
                  <i className="fas fa-unlock"></i>
                  Открыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainScreen;
